import { IMAGES } from "../lib/site";
import PageHero from "../components/site/PageHero";
import CTASection from "../components/site/CTASection";
import { Scissors, TreeDeciduous, Sprout, Leaf } from "lucide-react";

const AREAS = [
  {
    icon: Scissors,
    title: "Grass & Hedge Cutting",
    copy: "Regular mow rounds, precise edging, hedge shaping — for gardens that look kept every week of the year.",
  },
  {
    icon: TreeDeciduous,
    title: "Limb & Branch Removal",
    copy: "Safe removal of overhanging branches, dead limbs and problematic growth — with proper tools and full clean-up.",
  },
  {
    icon: Sprout,
    title: "General Upkeep",
    copy: "Weeding, tidying beds, path clearing, patio pressure-washing — the routine work that makes gardens feel loved.",
  },
  {
    icon: Leaf,
    title: "One-off Rescues",
    copy: "Overgrown garden after a move-in? Long-let cleanup? We reset even the wildest plots to a workable baseline.",
  },
];

export default function GardenMaintenance() {
  return (
    <>
      <PageHero
        overline="Outside the home"
        title="Garden Maintenance"
        subtitle="Grass, hedges, limb removal and general upkeep — Norfolk gardens kept looking their best."
        image={IMAGES.garden}
        testId="garden-hero"
      />

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline text-neutral-500">The service</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Gardens tended with a steady, seasonal rhythm.
            </h2>
            <p className="mt-8 text-neutral-600 text-lg leading-relaxed max-w-2xl">
              Whether you want a fortnightly mow round or a one-off reset after
              a long winter, we work to a schedule that fits your garden — not
              a rigid template.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
          {AREAS.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.title}
                className="bg-white p-10 md:p-12"
                data-testid={`gm-area-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <Icon size={28} strokeWidth={1.5} />
                <h3 className="mt-6 font-serif-display text-3xl leading-tight">
                  {a.title}
                </h3>
                <p className="mt-4 text-neutral-600 leading-relaxed">
                  {a.copy}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <p className="overline text-neutral-500 mb-4">Local knowledge</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
              Norfolk soil, Norfolk seasons — we know what your garden needs and when.
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed">
              Coastal, rural or town — every plot is treated on its own terms.
              We leave debris cleared, cuttings bagged, and the space usable
              the same day.
            </p>
          </div>
          <div className="md:col-span-7 order-1 md:order-2 img-hover">
            <img
              src={IMAGES.hedge}
              alt="Hedge and garden work"
              className="w-full h-[560px] object-cover"
            />
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
